import { useCallback, useEffect, useRef, useState } from 'react'

// Ported from Printscape's useStreamedText. A streamed answer arrives in
// bursts with stalls between them; showing it as it lands reads as freezes.
// This buffers what arrives and reveals it at a steady pace instead.

/** Ticks of backlog kept in hand to ride out a gap, about a third of a second. */
const CATCHUP_DIVISOR = 6
/** Floor in characters per tick, near reading pace. */
const MIN_CHARS_PER_TICK = 14
/** About 16 updates a second: every update re-parses the whole markdown answer. */
const TICK_MS = 60
/** flush() must never be able to hang a turn. */
const FLUSH_TIMEOUT_MS = 2000

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/** `push()` writes to a buffer and does not render; only the paced tick does. */
export function useStreamedText() {
  const [shown, setShown] = useState('')

  const bufferRef = useRef('')
  const shownRef = useRef('')
  const frameRef = useRef<number | null>(null)
  const lastTickRef = useRef(0)

  const stop = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [])

  const reveal = useCallback((text: string) => {
    shownRef.current = text
    setShown(text)
  }, [])

  const start = useCallback(() => {
    if (frameRef.current !== null) return

    const tick = () => {
      const backlog = bufferRef.current.length - shownRef.current.length

      if (backlog <= 0) {
        frameRef.current = null
        return
      }

      const now = performance.now()
      if (now - lastTickRef.current >= TICK_MS) {
        lastTickRef.current = now
        const step = Math.max(MIN_CHARS_PER_TICK, Math.ceil(backlog / CATCHUP_DIVISOR))
        reveal(bufferRef.current.slice(0, shownRef.current.length + step))
      }

      frameRef.current = requestAnimationFrame(tick)
    }

    frameRef.current = requestAnimationFrame(tick)
  }, [reveal])

  const push = useCallback(
    (text: string) => {
      if (!text) return
      bufferRef.current += text

      if (prefersReducedMotion()) {
        reveal(bufferRef.current)
        return
      }

      start()
    },
    [reveal, start],
  )

  /** Resolves once everything received is shown, so the tail never snaps into place. */
  const flush = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (bufferRef.current.length === shownRef.current.length) {
          resolve()
          return
        }

        const deadline = performance.now() + FLUSH_TIMEOUT_MS

        const check = () => {
          const drained = bufferRef.current.length === shownRef.current.length
          if (drained || performance.now() > deadline) {
            if (!drained) reveal(bufferRef.current)
            resolve()
            return
          }
          requestAnimationFrame(check)
        }

        start()
        requestAnimationFrame(check)
      }),
    [reveal, start],
  )

  const reset = useCallback(() => {
    stop()
    bufferRef.current = ''
    shownRef.current = ''
    lastTickRef.current = 0
    setShown('')
  }, [stop])

  useEffect(() => stop, [stop])

  return { shown, push, flush, reset }
}
